
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import FilterListIcon from "@mui/icons-material/FilterList";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";

function ContentToolbar({
  searchQuery,
  setSearchQuery,
  sortOption,
  setSortOption,
  sortDirection,
  setSortDirection,
  filterOption,
  setFilterOption,
  filterOptions,
  isLocked = false, // [NEW] Default to false
  onUpgrade = () => { }, // [NEW] Default no-op
}) {
  // ✅ Removed custom tooltip open state (sortTooltipOpen, filterTooltipOpen)

  // [NEW] Intercept interactions
  const handleInteraction = (e) => {
    if (isLocked) {
      e.preventDefault();
      e.stopPropagation();
      onUpgrade();
    }
  };

  const handleSortDirectionToggle = () => {
    if (isLocked) { // [NEW] Guard
      onUpgrade();
      return;
    }
    setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const sortIconTransform =
    sortDirection === "asc" ? "rotate(180deg) scaleX(-1)" : "none";

  return (
    <MDBox
      my={2}
      mx={2}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      borderRadius="16px"
      gap={2}
      sx={{
        backgroundColor: "#f0f2f5",
        padding: "14px",
        position: "relative", // [NEW] Needed for overlay
      }}
    >
      {/* 🔒 [NEW] Locked Overlay */}
      {isLocked && (
        <MDBox
          onClick={handleInteraction}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            zIndex: 10,
            cursor: "pointer",
            backgroundColor: "rgba(255,255,255,0.01)", // Transparent but captures clicks
            borderRadius: "16px",
          }}
        />
      )}

      {/* 🔍 Search Bar */}
      <MDInput
        type="text"
        placeholder="Search content"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          flexGrow: 1,
          "& .MuiInputBase-root": {
            flexGrow: 1,
            borderRadius: "12px",
            backgroundColor: "white !important",
            boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
            paddingLeft: "6px",
          },
        }}
      />

      {/* ⚙️ Sort and Filter Controls */}
      <MDBox display="flex" alignItems="center" gap={1}>
        {/* 🧭 Sort Dropdown */}
        <Tooltip
          title="Sort By"
          arrow
          placement="bottom-end"
        >
          <FormControl
            variant="outlined"
            sx={{
              minWidth: 150,
              height: "43px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ddd",
              },
            }}
          >
            <Select
              id="sort-select"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              displayEmpty
              sx={{ height: "100%" }}
            >
              <MenuItem value="date">Posted Date</MenuItem>
              <MenuItem value="engagement">Engagement</MenuItem>
              <MenuItem value="engagement_rate">Engagement Rate</MenuItem>
              <MenuItem value="reaction">Reactions</MenuItem>
            </Select>
          </FormControl>
        </Tooltip>

        {/* 🔄 Sort Direction */}
        <Tooltip title="Sort Order" arrow placement="bottom-end">
          <IconButton
            onClick={handleSortDirectionToggle}
            sx={{
              ml: -1,
              mr: 1,
              transform: sortIconTransform,
              transition: "transform 0.3s ease-in-out",
            }}
          >
            <SortIcon color="action" />
          </IconButton>
        </Tooltip>
        {/* 🧩 Filter Dropdown */}
        <Tooltip
          title="Filter Options"
          arrow
          placement="bottom-end"
        >
          <FormControl
            variant="outlined"
            sx={{
              minWidth: 150,
              height: "43px",
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#ffffff",
                borderRadius: "12px",
                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#ddd",
              },
            }}
          >
            <Select
              id="filter-select"
              value={filterOption}
              onChange={(e) => setFilterOption(e.target.value)}
              displayEmpty
              sx={{
                height: "100%",
                pr: "36px",
              }}
            >
              {filterOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Tooltip>

        {/* 🎛️ Filter Icon (outside dropdown) */}

        <IconButton
          disableRipple
          disableFocusRipple
          sx={{
            ml: -1,
            mr: 1,
            transition: "transform 0.2s ease-in-out",
            "&:hover": { transform: "scale(1.1)" },
            cursor: "default",
            pointerEvents: "none",
          }}
        >
          <FilterListIcon color="action" />
        </IconButton>

      </MDBox>
    </MDBox>
  );
}

ContentToolbar.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  setSearchQuery: PropTypes.func.isRequired,
  sortOption: PropTypes.string.isRequired,
  setSortOption: PropTypes.func.isRequired,
  sortDirection: PropTypes.oneOf(["asc", "desc"]).isRequired,
  setSortDirection: PropTypes.func.isRequired,
  filterOption: PropTypes.string.isRequired,
  setFilterOption: PropTypes.func.isRequired,
  filterOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLocked: PropTypes.bool, // [NEW]
  onUpgrade: PropTypes.func, // [NEW]
};

export default ContentToolbar;